import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RxHamburgerMenu } from "react-icons/rx";
import { IoCloseOutline } from "react-icons/io5";
import Flag from "react-world-flags";

import Logo from "../assets/Images/footer-1.png";
import "./PrimaryNavigationBar.css";

/** ------- SOLUTIONS DATA ------- */
const SOLUTION_CATEGORIES = [
    {
        key: "rnd",
        title: "Discovery Phase",
        showHero: false,
        hero: null,
        linkGroups: {
            preclinical: [
                {
                    label: "Toxicological Services & Risk Assessment",
                    to: "/Research-and-Development/ToxicologicalServicesAndAssessment",
                },
            ],
            clinical: [
                {
                    label: "Clinical Trial Application with Clinical Studies - Operations",
                    to: "/Discovery-Phase/Clinical/Clinical-Trial-Applications-with-Clinical-Studies-Operations",
                },
            ],
        },
    },
    {
        key: "DMCS",
        title: "CDISC Standardization & Data Management",
        hero: null,
        linkGroups: {
            preclinical: [
                {
                    label: "SEND Submission",
                    to: "/Data-Management-and-CDISC-Standardization/Preclinical/SEND",
                },
                {
                    label: "Flexible Data Capture Options for SEND Compliance",
                    to: "/CDISC-Standardization-&-Data-Management/Preclinical/Flexible-Data-Capture-Options-for-SEND-Compliance",
                },
            ],
            clinical: [
                {
                    label: "Clinical Data Management",
                    to: "/Departments/Statistics-and-Data-Science/Clinical-Data-Management-Services",
                },
                {
                    label: "CDASH, SDTM, ADaM CDISC Standards and CDISC Package",
                    to: "/CDISC-Standardization-&-Data-Management/Clinical/CDASH-SDTM-ADaM-CDISC-Package",
                },
                {
                    label: "Statistical Reporting & Integrated Analyses",
                    to: "/CDISC-Standardization-&-Data-Management/Clinical/Statistical-Reporting-&-Integrated-Analyses",
                },
            ],
        },
    },
    {
        key: "sdi",
        title: "Biostatistics & Data-Driven Inferences",
        hero: null,
        linkGroups: {
            preclinical: [
                {
                    label: "Biostatistical Support for Nonclinical Studies",
                    to: "/Biostatistics-&-Data-Driven-Inferences/Preclinical/Biostatistical-Support-for-Nonclinical-Studies",
                },
            ],
            clinical: [
                {
                    label: "Clinical Biostatistics",
                    to: "/Departments/Statistics-and-Data-Science/Clinical-Biostatistics",
                },
            ],
        },
    },
    {
        key: "RAC",
        title: "Regulatory Affairs and CMC",
        hero: null,
        linkGroups: {
            regulatoryAffairs: {
                label: "Regulatory Affairs",
                items: [
                    {
                        label: "Medical Device",
                        to: "/MedicalDevice",
                    },
                ],
            },
            cmc: {
                label: "CMC",
                items: [
                    // {
                    //     label: "CMC Biologics",
                    //     to: "/REGULATORY-AFFAIRS-AND-CMC/CMC-Biologics",
                    // },
                    {
                        label: "CMC Services",
                        to: "/REGULATORY-AFFAIRS-AND-CMC/CMC-Services",
                    },
                ],
            },
        },
    },
    {
        key: "MA",
        title: "Evidence Generation, Commercialization & Market Access",
        hero: null,
        linkGroups: {
            services: {
                label: "Services",
                items: [
                    {
                        label: "Commercialization Services",
                        to: "/Evidence-Generation-Commercialization-&-Market-Access/Commercialization-Services",
                    },
                    {
                        label: "Health Economics and Outcome Research",
                        to: "#",
                    },
                    {
                        label: "Market Access Strategy",
                        to: "#",
                    },
                    {
                        label: "Pricing and Reimbursement",
                        to: "#",
                    },
                ],
            },
            hta: {
                label: "Global HTA & Regulatory Bodies",
                items: [
                    { label: "Germany (AMNOG/IQWiG)", flagCode: "DE", to: "#" },
                    { label: "France (HAS)", flagCode: "FR", to: "#" },
                    { label: "United Kingdom (NICE)", flagCode: "GB", to: "#" },
                    { label: "European Union (EMA / EU-HTA)", flagCode: "EU", to: "#" },
                    { label: "United States (FDA)", flagCode: "US", to: "#" },
                    { label: "Japan (PMDA)", flagCode: "JP", to: "#" },
                    { label: "Australia (PBAC)", flagCode: "AU", to: "#" },
                    { label: "Canada (CADTH)", flagCode: "CA", to: "#" },
                ],
            },
        },
    },

];

/** ------- PRODUCTS DATA ------- */
const PRODUCT_ITEMS = [
    {
        key: "aletheion",
        title: "Aletheion™",
        tagline: "SEND automation for preclinical regulatory data operations.",
        description:
            "Aletheion™ supports structured nonclinical study data processing, SEND dataset generation, domain-level review, validation workflows, and submission-ready package preparation.",
        to: "/products/aletheion",
    },
    {
        key: "nebula",
        title: "Nebula™",
        tagline: "AI-enabled RBQM and clinical trial oversight platform.",
        description:
            "Nebula™ helps clinical operations teams identify risks, monitor key indicators, track site performance, and move from reactive trial management to proactive execution control.",
        to: "/products/nebula",
    },
    {
        key: "pronoia",
        title: "Pronoia Patient Intelligence™",
        tagline: "Patient cohort intelligence and population feasibility analytics.",
        description:
            "Pronoia Patient Intelligence™ helps sponsors understand where the right patients are, which sites may have access to them, and how inclusion and exclusion criteria affect the available study population.",
        to: "/products/pronoia-patient-intelligence",
    },
];

function humanizeKey(key) {
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function PrimaryNavigationBar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const [megaOpen, setMegaOpen] = useState(false);
    const [activeKey, setActiveKey] = useState(SOLUTION_CATEGORIES[0].key);

    const [productMegaOpen, setProductMegaOpen] = useState(false);
    const [activeProductKey, setActiveProductKey] = useState(PRODUCT_ITEMS[0].key);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNavClick = () => {
        setMegaOpen(false);
        setProductMegaOpen(false);
        setMobileOpen(false);
    };

    const activeProduct =
        PRODUCT_ITEMS.find((item) => item.key === activeProductKey) || PRODUCT_ITEMS[0];

    return (
        <header className={`ex-nav ${scrolled ? "ex-nav--scrolled" : ""}`}>
            <div className="ex-nav__wrap">
                <Link
                    to="/"
                    className="ex-nav__brand"
                    aria-label="Eupraxia"
                    onClick={handleNavClick}
                >
                    <img src={Logo} alt="Eupraxia" />
                </Link>

                <nav
                    className={`ex-links ${mobileOpen ? "is-open" : ""}`}
                    aria-label="Primary"
                >
                    <button
                        className="ex-links__close"
                        aria-label="Close menu"
                        onClick={() => setMobileOpen(false)}
                    >
                        <IoCloseOutline />
                    </button>

                    {/* SOLUTIONS */}
                    <div className={`ex-solutions ${megaOpen ? "is-open" : ""}`}>
                        <button
                            className="ex-solutions__trigger"
                            aria-expanded={megaOpen}
                            onClick={() => {
                                setMegaOpen((v) => !v);
                                setProductMegaOpen(false);
                            }}
                        >
                            Solutions
                        </button>

                        <div className="ex-mega" role="menu" aria-label="Solutions menu">
                            <div className="ex-mega__inner">
                                <div className="ex-mega__panels">
                                    <aside className="ex-rail" aria-label="Solution categories">
                                        <h4 className="ex-rail__title">Solutions</h4>

                                        <ul className="ex-rail__list">
                                            {SOLUTION_CATEGORIES.map((cat) => (
                                                <li
                                                    key={cat.key}
                                                    className={`ex-rail__item ${
                                                        activeKey === cat.key ? "is-active" : ""
                                                    }`}
                                                    onMouseEnter={() => setActiveKey(cat.key)}
                                                    onFocus={() => setActiveKey(cat.key)}
                                                    tabIndex={0}
                                                >
                                                    <span className="ex-rail__label">
                                                        {cat.title}
                                                    </span>
                                                    <span className="ex-rail__chev">›</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </aside>

                                    <section className="ex-detail" aria-live="polite">
                                        {SOLUTION_CATEGORIES.map((cat) => {
                                            const hasGroups = "linkGroups" in cat;
                                            const hasHero = !!(cat.hero && cat.showHero !== false);
                                            const noHero = !hasHero;

                                            return (
                                                <article
                                                    key={cat.key}
                                                    className={`ex-detail__panel ${
                                                        activeKey === cat.key ? "show" : ""
                                                    } ${
                                                        hasGroups ? "has-two-col-links" : ""
                                                    } ${noHero ? "no-hero" : ""}`}
                                                >
                                                    <div className="ex-detail__grid">
                                                        {hasHero && (
                                                            <>
                                                                <div className="ex-detail__visual">
                                                                    {cat.hero.img ? (
                                                                        <img src={cat.hero.img} alt="" />
                                                                    ) : (
                                                                        <div
                                                                            className="ex-visual__ph"
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                </div>

                                                                <div className="ex-detail__copy">
                                                                    <h3>{cat.hero.heading}</h3>
                                                                    <p>{cat.hero.copy}</p>

                                                                    <Link
                                                                        to={cat.hero.cta.to}
                                                                        className="ex-pillcta"
                                                                        onClick={handleNavClick}
                                                                    >
                                                                        {cat.hero.cta.label}
                                                                    </Link>
                                                                </div>
                                                            </>
                                                        )}

                                                        {hasGroups ? (
                                                            <div
                                                                className={[
                                                                    "ex-detail__links",
                                                                    "ex-detail__links--grid",
                                                                    cat.key === "MA"
                                                                        ? "ex-detail__links--ma"
                                                                        : "",
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(" ")}
                                                            >
                                                                <h5 className="ex-links__header">
                                                                    {cat.title}
                                                                </h5>

                                                                {Object.entries(cat.linkGroups ?? {}).map(
                                                                    ([groupKey, group], idx) => {
                                                                        const isArray = Array.isArray(group);
                                                                        const items = isArray
                                                                            ? group
                                                                            : group?.items ?? [];
                                                                        const label = isArray
                                                                            ? humanizeKey(groupKey)
                                                                            : group?.label ?? humanizeKey(groupKey);

                                                                        if (!items.length) return null;

                                                                        const isMAFlags =
                                                                            cat.key === "MA" && groupKey === "hta";

                                                                        return (
                                                                            <div
                                                                                className="ex-links__col"
                                                                                key={groupKey}
                                                                                data-col-index={idx}
                                                                            >
                                                                                <h5>{label}</h5>

                                                                                <ul>
                                                                                    {items.map((item, i) => (
                                                                                        <li key={`${groupKey}-${i}`}>
                                                                                            {isMAFlags && item.flagCode && (
                                                                                                <Flag
                                                                                                    code={item.flagCode}
                                                                                                    className="ex-flag"
                                                                                                />
                                                                                            )}

                                                                                            {isMAFlags ? (
                                                                                                <span className="ma-flag-label">
                                                                                                    {item.label}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <Link
                                                                                                    to={item.to ?? "#"}
                                                                                                    onClick={handleNavClick}
                                                                                                >
                                                                                                    {item.label}
                                                                                                </Link>
                                                                                            )}
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="ex-detail__links">
                                                                <h5>{cat.title}</h5>

                                                                <ul>
                                                                    {(cat.links ?? []).map((l, i) => (
                                                                        <li key={i}>
                                                                            <Link
                                                                                to={l.to}
                                                                                onClick={handleNavClick}
                                                                            >
                                                                                {l.label}
                                                                            </Link>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PRODUCTS DESKTOP MEGA MENU */}
                    <div
                        className={`ex-products ${productMegaOpen ? "is-open" : ""}`}
                    >
                        <button
                            className="ex-products__trigger"
                            aria-expanded={productMegaOpen}
                            onClick={() => {
                                setProductMegaOpen((v) => !v);
                                setMegaOpen(false);
                            }}
                        >
                            Products
                        </button>

                        <div
                            className="ex-productsMega"
                            role="menu"
                            aria-label="Products menu"
                        >
                            <div className="ex-productsMega__inner">
                                <div className="ex-productsMega__panels">
                                    <aside
                                        className="ex-productsRail"
                                        aria-label="Product list"
                                    >
                                        <h4 className="ex-productsRail__title">
                                            Products
                                        </h4>

                                        <ul className="ex-productsRail__list">
                                            {PRODUCT_ITEMS.map((product) => (
                                                <li
                                                    key={product.key}
                                                    className={`ex-productsRail__item ${
                                                        activeProductKey === product.key
                                                            ? "is-active"
                                                            : ""
                                                    }`}
                                                    onMouseEnter={() =>
                                                        setActiveProductKey(product.key)
                                                    }
                                                    onFocus={() =>
                                                        setActiveProductKey(product.key)
                                                    }
                                                    tabIndex={0}
                                                >
                                                    <span className="ex-productsRail__label">
                                                        {product.title}
                                                    </span>
                                                    <span className="ex-productsRail__chev">
                                                        ›
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </aside>

                                    <section
                                        className="ex-productDetail"
                                        aria-live="polite"
                                    >
                                        <article className="ex-productDetail__card">
                                            <span className="ex-productDetail__eyebrow">
                                                Product
                                            </span>

                                            <h3>{activeProduct.title}</h3>

                                            <p className="ex-productDetail__tagline">
                                                {activeProduct.tagline}
                                            </p>

                                            <p className="ex-productDetail__description">
                                                {activeProduct.description}
                                            </p>

                                            <Link
                                                to={activeProduct.to}
                                                className="ex-productDetail__cta"
                                                onClick={handleNavClick}
                                            >
                                                Learn More
                                            </Link>
                                        </article>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PRODUCTS MOBILE LINK */}
                    <Link
                        to="/Product"
                        className="ex-link ex-productsMobileLink"
                        onClick={handleNavClick}
                    >
                        Products
                    </Link>

                    <Link
                        to="/AboutUs"
                        className="ex-link"
                        onClick={handleNavClick}
                    >
                        About Us
                    </Link>

                    <Link
                        to="/careers"
                        className="ex-link"
                        onClick={handleNavClick}
                    >
                        Careers
                    </Link>

                    <Link
                        to="/ContactUs"
                        className="ex-link ex-link--primary"
                        onClick={handleNavClick}
                    >
                        Contact Us
                    </Link>
                </nav>

                <button
                    className="ex-burger"
                    aria-label="Open menu"
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    <RxHamburgerMenu />
                </button>
            </div>
        </header>
    );
}